import { supabase } from './supabase';
import { generateTicketNumber } from './utils';

export interface SupportTicket {
  id: string;
  user_id: string;
  ticket_number: string;
  title: string;
  description: string;
  category: string;
  priority: 'low' | 'medium' | 'high' | 'urgent' | 'critical';
  status: 'open' | 'in_progress' | 'waiting_for_user' | 'waiting_for_third_party' | 'resolved' | 'closed' | 'cancelled';
  assigned_to?: string;
  assigned_at?: string;
  resolved_at?: string;
  closed_at?: string;
  resolution_notes?: string;
  user_satisfaction_rating?: number;
  tags?: string[];
  metadata?: any;
  created_at: string;
  updated_at: string;
}

export interface BugReport {
  id: string;
  ticket_id: string;
  browser?: string;
  browser_version?: string;
  operating_system?: string;
  os_version?: string;
  device_type?: 'desktop' | 'tablet' | 'mobile' | 'other';
  screen_resolution?: string;
  error_message?: string;
  error_stack_trace?: string;
  console_logs?: string;
  network_logs?: string;
  steps_to_reproduce?: string;
  expected_behavior?: string;
  actual_behavior?: string;
  frequency?: 'always' | 'sometimes' | 'rarely' | 'once';
  severity?: 'low' | 'medium' | 'high' | 'critical';
  created_at: string;
  updated_at: string;
}

export interface FeatureRequest {
  id: string;
  ticket_id: string;
  feature_name: string;
  feature_description: string;
  use_case?: string;
  benefit_description?: string;
  target_audience?: string;
  estimated_impact?: 'low' | 'medium' | 'high' | 'critical';
  implementation_complexity?: 'low' | 'medium' | 'high' | 'very_high';
  related_features?: string[];
  created_at: string;
  updated_at: string;
}

export interface UserFeedback {
  id: string;
  user_id: string;
  feedback_type: 'general' | 'feature_feedback' | 'ui_ux' | 'performance' | 'content' | 'suggestion' | 'compliment' | 'complaint';
  title: string;
  description: string;
  rating?: number;
  module?: string;
  tags?: string[];
  is_anonymous: boolean;
  allow_contact: boolean;
  status: 'submitted' | 'reviewed' | 'in_progress' | 'implemented' | 'declined' | 'closed';
  admin_notes?: string;
  created_at: string;
  updated_at: string;
}

export interface SupportCategory {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  color: string;
  is_active: boolean;
  sort_order: number;
  parent_category_id?: string;
  created_at: string;
  updated_at: string;
}

export interface KnowledgeBaseArticle {
  id: string;
  title: string;
  content: string;
  category_id?: string;
  tags?: string[];
  is_published: boolean;
  is_featured: boolean;
  view_count: number;
  helpful_count: number;
  not_helpful_count: number;
  author_id?: string;
  published_at?: string;
  created_at: string;
  updated_at: string;
}

export interface TicketTemplate {
  id: string;
  name: string;
  category: string;
  title_template: string;
  description_template: string;
  priority: string;
  tags?: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateTicketData {
  title: string;
  description: string;
  category: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent' | 'critical';
  tags?: string[];
  metadata?: any;
}

export interface CreateBugReportData {
  ticket_id: string;
  browser?: string;
  browser_version?: string;
  operating_system?: string;
  os_version?: string;
  device_type?: 'desktop' | 'tablet' | 'mobile' | 'other';
  screen_resolution?: string;
  error_message?: string;
  error_stack_trace?: string;
  console_logs?: string;
  network_logs?: string;
  steps_to_reproduce?: string;
  expected_behavior?: string;
  actual_behavior?: string;
  frequency?: 'always' | 'sometimes' | 'rarely' | 'once';
  severity?: 'low' | 'medium' | 'high' | 'critical';
}

export interface CreateFeatureRequestData {
  ticket_id: string;
  feature_name: string;
  feature_description: string;
  use_case?: string;
  benefit_description?: string;
  target_audience?: string;
  estimated_impact?: 'low' | 'medium' | 'high' | 'critical';
  implementation_complexity?: 'low' | 'medium' | 'high' | 'very_high';
  related_features?: string[];
}

export interface CreateFeedbackData {
  feedback_type: 'general' | 'feature_feedback' | 'ui_ux' | 'performance' | 'content' | 'suggestion' | 'compliment' | 'complaint';
  title: string;
  description: string;
  rating?: number;
  module?: string;
  tags?: string[];
  is_anonymous?: boolean;
  allow_contact?: boolean;
}

class SupportService {
  // Support Tickets
  async createTicket(data: CreateTicketData): Promise<SupportTicket> {
    const ticketNumber = await generateTicketNumber();
    
    const { data: ticket, error } = await supabase
      .from('support_tickets')
      .insert([{
        ...data,
        ticket_number: ticketNumber,
        priority: data.priority || 'medium'
      }])
      .select()
      .single();

    if (error) throw error;
    return ticket;
  }

  async getTickets(): Promise<SupportTicket[]> {
    const { data, error } = await supabase
      .from('support_tickets')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  async getTicket(id: string): Promise<SupportTicket> {
    const { data, error } = await supabase
      .from('support_tickets')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  }

  async updateTicket(id: string, updates: Partial<SupportTicket>): Promise<SupportTicket> {
    const { data, error } = await supabase
      .from('support_tickets')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async closeTicket(id: string, resolutionNotes?: string): Promise<SupportTicket> {
    return this.updateTicket(id, {
      status: 'closed',
      closed_at: new Date().toISOString(),
      resolution_notes: resolutionNotes
    });
  }

  // Bug Reports
  async createBugReport(data: CreateBugReportData): Promise<BugReport> {
    const { data: bugReport, error } = await supabase
      .from('bug_reports')
      .insert([data])
      .select()
      .single();

    if (error) throw error;
    return bugReport;
  }

  async getBugReport(ticketId: string): Promise<BugReport | null> {
    const { data, error } = await supabase
      .from('bug_reports')
      .select('*')
      .eq('ticket_id', ticketId)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  }

  // Feature Requests
  async createFeatureRequest(data: CreateFeatureRequestData): Promise<FeatureRequest> {
    const { data: featureRequest, error } = await supabase
      .from('feature_requests')
      .insert([data])
      .select()
      .single();

    if (error) throw error;
    return featureRequest;
  }

  async getFeatureRequest(ticketId: string): Promise<FeatureRequest | null> {
    const { data, error } = await supabase
      .from('feature_requests')
      .select('*')
      .eq('ticket_id', ticketId)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  }

  // User Feedback
  async createFeedback(data: CreateFeedbackData): Promise<UserFeedback> {
    const { data: feedback, error } = await supabase
      .from('user_feedback')
      .insert([{
        ...data,
        is_anonymous: data.is_anonymous || false,
        allow_contact: data.allow_contact !== false
      }])
      .select()
      .single();

    if (error) throw error;
    return feedback;
  }

  async getFeedback(): Promise<UserFeedback[]> {
    const { data, error } = await supabase
      .from('user_feedback')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  // Support Categories
  async getCategories(): Promise<SupportCategory[]> {
    const { data, error } = await supabase
      .from('support_categories')
      .select('*')
      .eq('is_active', true)
      .order('sort_order', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  // Knowledge Base
  async getKnowledgeBaseArticles(): Promise<KnowledgeBaseArticle[]> {
    const { data, error } = await supabase
      .from('support_knowledge_base')
      .select('*')
      .eq('is_published', true)
      .order('is_featured', { ascending: false })
      .order('view_count', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  async getKnowledgeBaseArticle(id: string): Promise<KnowledgeBaseArticle> {
    const { data, error } = await supabase
      .from('support_knowledge_base')
      .select('*')
      .eq('id', id)
      .eq('is_published', true)
      .single();

    if (error) throw error;
    return data;
  }

  async searchKnowledgeBase(query: string): Promise<KnowledgeBaseArticle[]> {
    const { data, error } = await supabase
      .from('support_knowledge_base')
      .select('*')
      .eq('is_published', true)
      .or(`title.ilike.%${query}%,content.ilike.%${query}%`)
      .order('is_featured', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  // Ticket Templates
  async getTicketTemplates(): Promise<TicketTemplate[]> {
    const { data, error } = await supabase
      .from('support_ticket_templates')
      .select('*')
      .eq('is_active', true)
      .order('name', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  async getTicketTemplate(category: string): Promise<TicketTemplate | null> {
    const { data, error } = await supabase
      .from('support_ticket_templates')
      .select('*')
      .eq('category', category)
      .eq('is_active', true)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  }

  // Utility functions
  async getSystemInfo(): Promise<any> {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      screenResolution: `${screen.width}x${screen.height}`,
      windowSize: `${window.innerWidth}x${window.innerHeight}`,
      timestamp: new Date().toISOString()
    };
  }

  async collectBrowserInfo(): Promise<any> {
    const systemInfo = await this.getSystemInfo();
    
    // Parse browser info from user agent
    const userAgent = systemInfo.userAgent;
    let browser = 'Unknown';
    let browserVersion = 'Unknown';
    
    if (userAgent.includes('Chrome')) {
      browser = 'Chrome';
      const match = userAgent.match(/Chrome\/(\d+\.\d+)/);
      if (match) browserVersion = match[1];
    } else if (userAgent.includes('Firefox')) {
      browser = 'Firefox';
      const match = userAgent.match(/Firefox\/(\d+\.\d+)/);
      if (match) browserVersion = match[1];
    } else if (userAgent.includes('Safari')) {
      browser = 'Safari';
      const match = userAgent.match(/Version\/(\d+\.\d+)/);
      if (match) browserVersion = match[1];
    } else if (userAgent.includes('Edge')) {
      browser = 'Edge';
      const match = userAgent.match(/Edge\/(\d+\.\d+)/);
      if (match) browserVersion = match[1];
    }

    // Parse OS info
    let operatingSystem = 'Unknown';
    let osVersion = 'Unknown';
    
    if (userAgent.includes('Windows')) {
      operatingSystem = 'Windows';
      const match = userAgent.match(/Windows NT (\d+\.\d+)/);
      if (match) osVersion = match[1];
    } else if (userAgent.includes('Mac')) {
      operatingSystem = 'macOS';
      const match = userAgent.match(/Mac OS X (\d+[._]\d+)/);
      if (match) osVersion = match[1].replace('_', '.');
    } else if (userAgent.includes('Linux')) {
      operatingSystem = 'Linux';
    } else if (userAgent.includes('Android')) {
      operatingSystem = 'Android';
      const match = userAgent.match(/Android (\d+\.\d+)/);
      if (match) osVersion = match[1];
    } else if (userAgent.includes('iOS')) {
      operatingSystem = 'iOS';
      const match = userAgent.match(/OS (\d+[._]\d+)/);
      if (match) osVersion = match[1].replace('_', '.');
    }

    // Determine device type
    let deviceType: 'desktop' | 'tablet' | 'mobile' | 'other' = 'desktop';
    if (userAgent.includes('Mobile')) {
      deviceType = 'mobile';
    } else if (userAgent.includes('Tablet') || (screen.width >= 768 && screen.width <= 1024)) {
      deviceType = 'tablet';
    }

    return {
      browser,
      browser_version: browserVersion,
      operating_system: operatingSystem,
      os_version: osVersion,
      device_type: deviceType,
      screen_resolution: systemInfo.screenResolution,
      ...systemInfo
    };
  }
}

export const supportService = new SupportService(); 